import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Search, Filter, ShoppingBag, Plus, MapPin, Clock } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../theme/ThemeContext';
import { useCurrency } from '../context/CurrencyContext';
import API, { BASE_URL } from '../services/api';
import { addToCart } from '../redux/slices/cartSlice';
import BranchSelector from '../components/BranchSelector';
import { isBranchOpen } from '../utils/branchUtils';

const Products = () => {
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const { formatPrice } = useCurrency();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryId = searchParams.get('category');
  const { user } = useSelector((state) => state.auth);

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(categoryId || 'all');
  const [showBranchModal, setShowBranchModal] = useState(false);

  const { selectedBranch } = useSelector((state) => state.branch);
  const isOpen = selectedBranch ? isBranchOpen(selectedBranch.operatingHours) : true;

  const renderName = (nameObj) => {
    if (typeof nameObj === 'string') return nameObj;
    return nameObj?.[i18n.language] || nameObj?.en || '';
  };

  const renderDescription = (descObj) => {
    if (typeof descObj === 'string') return descObj;
    return descObj?.[i18n.language] || descObj?.en || '';
  };

  useEffect(() => {
    if (!selectedBranch) {
      setShowBranchModal(true);
    }
  }, [selectedBranch]);

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedBranch) return;
      
      setLoading(true);
      try {
        const branchParam = `?branch=${selectedBranch._id}`;
        const [prodRes, catRes] = await Promise.all([
          API.get(`/products${branchParam}`),
          API.get(`/categories${branchParam}`)
        ]);
        setProducts(prodRes.data.data);
        setCategories(catRes.data.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedBranch]);

  useEffect(() => {
    if (categoryId) setSelectedCategory(categoryId);
  }, [categoryId]);

  const filteredProducts = products.filter(prod => {
    const name = renderName(prod.name);
    const matchesSearch = name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || prod.category?._id === selectedCategory || prod.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleAddToCart = (e, product) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isOpen) return;

    if (!user) {
      navigate('/login?redirect=/products');
      return;
    }
    
    // Use the same default logic as ProductDetails
    const defaultVariant = { _id: 'regular', name: 'Regular', price: product.basePrice };
    
    dispatch(addToCart({
      _id: product._id,
      name: product.name,
      image: product.image,
      quantity: 1,
      variant: defaultVariant,
      selectedAddOns: [],
      pricePerUnit: product.basePrice
    }));
  };

  return (
    <div className="space-y-8 md:space-y-12 animate-in fade-in duration-500">
      {/* Branch Selection Modal */}
      {showBranchModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 md:p-6 bg-black/60 backdrop-blur-sm">
          <div 
            className="w-full max-w-2xl rounded-[32px] md:rounded-[48px] overflow-hidden shadow-2xl border animate-in zoom-in duration-300"
            style={{ backgroundColor: theme.card, borderColor: theme.border }}
          >
            <div className="p-8 md:p-12 space-y-8">
              <div className="space-y-2 text-center">
                <div className="w-16 h-16 rounded-3xl flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: theme.primary }}>
                  <MapPin size={32} style={{ color: theme.background }} />
                </div>
                <h2 className="text-2xl md:text-4xl font-black uppercase tracking-tighter" style={{ color: theme.text }}>
                  {t('home.select_location_title', 'Select Your')} <span style={{ color: theme.primary }}>Optimist.</span>
                </h2>
                <p className="text-[10px] md:text-sm font-bold uppercase tracking-widest opacity-40">
                  {t('home.select_location_subtitle_products', 'Pick a location to see our local menu')}
                </p>
              </div>

              <BranchSelector onSelect={() => setShowBranchModal(false)} />

              {selectedBranch && (
                <button 
                  onClick={() => setShowBranchModal(false)}
                  className="w-full py-4 text-[10px] font-black uppercase tracking-widest opacity-40 hover:opacity-100 transition-opacity"
                >
                  {t('home.keep_location', 'Keep')} {renderName(selectedBranch.name)}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header & Filters */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 px-1 md:px-0">
        <div className="space-y-1 md:space-y-2 text-center md:text-left">
          {selectedBranch && (
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mb-2 md:mb-4">
              <div 
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full cursor-pointer hover:opacity-80 transition-opacity shadow-sm"
                style={{ backgroundColor: theme.surface }}
                onClick={() => setShowBranchModal(true)}
              >
                <MapPin size={10} style={{ color: theme.primary }} />
                <span className="text-[8px] md:text-[10px] font-black uppercase tracking-widest" style={{ color: theme.text }}>
                  {t('home.ordering_from', 'Ordering from')}: {renderName(selectedBranch.name)}
                </span>
              </div>
              
              <div 
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full shadow-sm ${isOpen ? 'bg-green-500/10' : 'bg-red-500/10'}`}
              >
                <div className={`w-1.5 h-1.5 rounded-full ${isOpen ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                <span className={`text-[8px] md:text-[10px] font-black uppercase tracking-widest ${isOpen ? 'text-green-500' : 'text-red-500'}`}>
                  {isOpen ? t('common.open', 'Open') : t('common.closed', 'Closed')}
                </span>
              </div>
            </div>
          )}
          <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tighter leading-tight" style={{ color: theme.primary }}>{t('common.our_menu', 'Our Menu')}</h1>
          {!isOpen && (
            <p className="text-[10px] md:text-sm font-bold uppercase tracking-widest text-red-500 animate-pulse">
              {t('products.closed_warning', 'Branch is currently closed. Ordering is disabled.')}
            </p>
          )}
          {isOpen && <p className="text-[10px] md:text-sm font-bold uppercase tracking-widest opacity-60 leading-relaxed">{t('products.freshly_brewed', 'Freshly brewed just for you')}</p>}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 md:gap-4 w-full md:max-w-2xl">
          <div 
            className="flex-grow flex items-center px-4 py-3 md:py-4 rounded-xl md:rounded-2xl border transition-all gap-3"
            style={{ backgroundColor: theme.card, borderColor: theme.border }}
          >
            <Search size={16} md={18} className="opacity-40 shrink-0" />
            <input 
              type="text" 
              placeholder={t('products.search_placeholder', 'Search coffee...')}
              className="bg-transparent border-none outline-none w-full font-bold text-xs md:text-sm"
              style={{ color: theme.text }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div 
            className="flex items-center px-4 py-3 md:py-4 rounded-xl md:rounded-2xl border transition-all gap-3"
            style={{ backgroundColor: theme.card, borderColor: theme.border }}
          >
            <Filter size={14} md={16} className="opacity-40 shrink-0" />
            <select 
              className="bg-transparent border-none outline-none font-bold text-xs md:text-sm cursor-pointer min-w-[100px] w-full"
              style={{ color: theme.text }}
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setSearchParams(e.target.value === 'all' ? {} : { category: e.target.value });
              }}
            >
              <option value="all" style={{ backgroundColor: theme.card, color: theme.text }}>{t('products.all_categories', 'All Categories')}</option>
              {categories.map(cat => (
                <option key={cat._id} value={cat._id} style={{ backgroundColor: theme.card, color: theme.text }}>{renderName(cat.name)}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Categories Bar */}
      <div className="flex items-center gap-3 md:gap-4 overflow-x-auto pb-4 no-scrollbar px-1 md:px-0">
        <button 
          onClick={() => {
            setSelectedCategory('all');
            setSearchParams({});
          }}
          className={`px-5 md:px-6 py-2.5 md:py-3 rounded-xl md:rounded-2xl font-black uppercase tracking-widest text-[9px] md:text-[10px] whitespace-nowrap transition-all ${selectedCategory === 'all' ? 'shadow-lg' : 'opacity-40 hover:opacity-100'}`}
          style={{ 
            backgroundColor: selectedCategory === 'all' ? theme.primary : theme.surface,
            color: selectedCategory === 'all' ? theme.background : theme.text
          }}
        >
          {t('common.all', 'All')}
        </button>
        {categories.map(cat => (
          <button 
            key={cat._id}
            onClick={() => {
              setSelectedCategory(cat._id);
              setSearchParams({ category: cat._id });
            }}
            className={`px-5 md:px-6 py-2.5 md:py-3 rounded-xl md:rounded-2xl font-black uppercase tracking-widest text-[9px] md:text-[10px] whitespace-nowrap transition-all ${selectedCategory === cat._id ? 'shadow-lg' : 'opacity-40 hover:opacity-100'}`}
            style={{ 
              backgroundColor: selectedCategory === cat._id ? theme.primary : theme.surface,
              color: selectedCategory === cat._id ? theme.background : theme.text
            }}
          >
            {renderName(cat.name)}
          </button>
        ))}
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-8 px-1 md:px-0">
        {loading ? (
          [...Array(8)].map((_, i) => (
            <div key={i} className="h-64 md:h-96 rounded-2xl md:rounded-[32px] animate-pulse" style={{ backgroundColor: theme.surface }} />
          ))
        ) : filteredProducts.length === 0 ? (
          <div className="col-span-full py-20 text-center">
            <ShoppingBag size={48} md={64} className="mx-auto opacity-10 mb-4" />
            <p className="font-black uppercase tracking-widest opacity-40 text-xs md:text-base">{t('products.no_items_found', 'No items found')}</p>
          </div>
        ) : (
          filteredProducts.map((prod) => (
            <div 
              key={prod._id}
              className="group p-3 md:p-6 rounded-2xl md:rounded-[32px] shadow-xl transition-all duration-500 hover:-translate-y-2 border relative flex flex-col"
              style={{ backgroundColor: theme.card, borderColor: theme.border }}
            >
              <div className="aspect-square rounded-xl md:rounded-2xl overflow-hidden mb-4 md:mb-6 relative">
                <img 
                  src={prod.image ? (prod.image.startsWith('http') ? prod.image : `${BASE_URL}${prod.image}`) : 'https://images.unsplash.com/photo-1559056199-641a0ac8b55e?q=80&w=400&auto=format&fit=crop'} 
                  alt={renderName(prod.name)}
                  className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${!isOpen ? 'grayscale opacity-70' : ''}`}
                />
                {isOpen && (
                  <button 
                    onClick={(e) => handleAddToCart(e, prod)}
                    className="absolute bottom-2 end-2 md:bottom-4 md:end-4 p-3 md:p-4 rounded-lg md:rounded-2xl shadow-2xl transition-all active:scale-90 md:translate-y-4 md:opacity-0 group-hover:translate-y-0 group-hover:opacity-100"
                    style={{ backgroundColor: theme.primary, color: theme.background }}
                  >
                    <Plus size={16} md={20} />
                  </button>
                )}
                {!isOpen && (
                  <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center p-4">
                    <div className="bg-white/90 px-3 py-1.5 rounded-xl flex items-center gap-2">
                      <Clock size={12} className="text-red-500" />
                      <span className="text-[8px] md:text-[10px] font-black uppercase text-red-500">{t('common.closed', 'Closed')}</span>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="space-y-1 md:space-y-2 flex-grow flex flex-col">
                <div className="flex items-baseline justify-between gap-2 min-w-0">
                  <h3 className="text-[10px] md:text-base font-black uppercase tracking-tight truncate leading-tight min-w-0" style={{ color: theme.text }}>
                    {renderName(prod.name)}
                  </h3>
                  <span className="font-black text-[9px] md:text-sm shrink-0 whitespace-nowrap" style={{ color: theme.accent }}>
                    {formatPrice(prod.basePrice)}
                  </span>
                </div>
                <p className="text-[8px] md:text-xs font-bold opacity-50 line-clamp-2 leading-relaxed" style={{ color: theme.text }}>{renderDescription(prod.description)}</p>
                <div className="pt-3 md:pt-4 mt-auto flex items-center justify-between gap-2">
                  <span className="text-[7px] md:text-[10px] font-black uppercase tracking-widest opacity-40 truncate">
                    {typeof prod.category === 'object' ? renderName(prod.category.name) : renderName(categories.find(c => c._id === prod.category)?.name)}
                  </span>
                  <Link 
                    to={`/product/${prod._id}`}
                    className="text-[7px] md:text-[10px] font-black uppercase tracking-widest transition-colors hover:underline whitespace-nowrap"
                    style={{ color: theme.primary }}
                  >
                    {t('common.details', 'Details')}
                  </Link>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Products;
